import { getAssetsSortBy, getAssetsSortDirection } from "signoxe/selectors/ui";
import { optionalToJS } from "./utils";
import { getCampaigns } from "signoxe/selectors/campaigns";

export const assetFromId = (state, id) =>
  state.entities.getIn(["assets", id.toString()]);

const getSortFieldFromState = (state) => (asset) => {
  const sortBy = getAssetsSortBy(state);
  if (sortBy === "NAME") return asset.get("name");
  if (sortBy === "TYPE") return asset.get("type");
  if (sortBy === "ID") return asset.get("id");
  return null;
};

const assetsFromIds = (state, ids) => {
  const reversed = getAssetsSortDirection(state);
  const assets = ids
    .map((id) => assetFromId(state, id))
    .sortBy(getSortFieldFromState(state));
  if (reversed) return assets.reverse();
  else return assets;
};

const assetsAll = (state) => assetsFromIds(state, state.assets.ids);

const assetHasTags = (asset, tags) => {
  return tags.isSubset(asset.get("tags", []));
};

const videoAssetSize = (asset) => {
  const videoData = asset.getIn(["metadata", "video_streams", "0"]);
  if (videoData) {
    return {
      width: videoData.get("Width"),
      height: videoData.get("Height"),
    };
  }
};

const imageAssetSize = (asset) => {
  const imageData = asset.getIn(["metadata", "File"]);
  if (imageData) {
    return {
      width: imageData.get("Width"),
      height: imageData.get("Height"),
    };
  }
};

export const assetSize = (asset) => {
  if (asset.get("type") === "IMAGE") return imageAssetSize(asset);
  else if (asset.get("type") === "VIDEO") return videoAssetSize(asset);
};

const assetHasAspect = (asset, aspect) => {
  if (!aspect) return true;
  const size = assetSize(asset);
  if (size) {
    const ratio = size.width / size.height;

    return (
      (aspect === "PORTRAIT" && ratio <= 1) ||
      (aspect === "LANDSCAPE" && ratio >= 1)
    );
  } else return false;
};

const assetMatchesQuery = (asset, query) =>
  asset.get("name").toLowerCase().includes(query) ||
  asset.get("tags", []).includes(query);

const assetIsOfType = (asset, type) =>
  type === null || asset.get("type", "").toLowerCase() === type;

const assetFilterFunction = (query, type, aspect, tags) => (asset) =>
  assetMatchesQuery(asset, query) &&
  assetIsOfType(asset, type) &&
  assetHasAspect(asset, aspect) &&
  assetHasTags(asset, tags);

const filteredAssetsByQuery = (state, query) => {
  let filteredAssets = assetsAll(state);
  const type = getFilterByType(state);
  const aspect = getFilterByAspect(state);
  const tags = selectedTags(state);

  query = query.trim().toLowerCase();

  if (query !== "" || type || aspect || tags.size > 0) {
    filteredAssets = filteredAssets.filter(
      assetFilterFunction(query, type, aspect, tags)
    );
  }

  return filteredAssets;
};

export const getFilteredAssetsByCampaign = (state, query) => {
  let filteredAssets = getFilteredAssetsByQuery(state, query);
  let campaigns = getCampaigns(state);
  let assetIds = [];
  campaigns.map((campaign) => {
    if (campaign.assets && campaign.assets.length > 0) {
      campaign.assets.map((asset) => assetIds.push(asset.id));
    }
  });
  filteredAssets = filteredAssets.filter(
    (asset) => !assetIds.includes(asset.id)
  );
  return filteredAssets;
};

const filteredAssets = (state) => {
  return filteredAssetsByQuery(state, state.uiState.searchQuery);
};

const filteredCampaignAssets = (state) =>
  getFilteredAssetsByCampaign(state, state.uiState.searchQuery);

export const getFilteredAssets = (state) => filteredAssets(state).toJS();
export const getCampaignFilteredAssets = (state) =>
  filteredCampaignAssets(state).toJS();

export const getFilteredAssetsByQuery = (state, query) =>
  filteredAssetsByQuery(state, query).toJS();

export const getAsset = (state, id) => optionalToJS(assetFromId(state, id));

const webAssetTemplatesAll = (state) => state.entities.get("webAssetTemplates");

const customTemplates = (state) =>
  state.entities.get("customTemplates").toList();


const scrollerTemplates = (state) =>
state.entities.get("scrollerTemplates").toList();


const webAssetTemplates = (state) =>
  webAssetTemplatesAll(state)
    .filter((template) => !template.get("data_support"))
    .toList();

const twitterTemplates = (state) =>
  webAssetTemplatesAll(state)
    .filter((template) => !template.get("data_support"))
    .filter((template) => template.get("name").startsWith("Twitter"))
    .toList();

const weatherTemplates = (state) =>
  webAssetTemplatesAll(state)
    .filter((template) => !template.get("data_support"))
    .filter((template) => template.get("name").startsWith("Weather"))
    .toList();

const youtubeTemplates = (state) =>
  webAssetTemplatesAll(state)
    .filter((template) => !template.get("data_support"))
    .filter((template) => template.get("name").startsWith("Youtube"))
    .toList();

const googleTemplates = (state) =>
  webAssetTemplatesAll(state)
    .filter((template) => !template.get("data_support"))
    .filter((template) => template.get("name").startsWith("Google Sheet"))
    .toList();

const calendarAssetTemplates = (state) =>
  webAssetTemplatesAll(state)
    .filter((template) => template.get("calendar_support"))
    .toList();

const dataAssetTemplates = (state) =>
  state.entities
    .get("webAssetTemplates")
    .filter((template) => template.get("data_support"))
    .toList();

export const getWebAssetTemplates = (state) => webAssetTemplates(state).toJS();

export const getTwitterTemplates = (state) => twitterTemplates(state).toJS();

export const getWeatherTemplates = (state) => weatherTemplates(state).toJS();

export const getGoogleTemplates = (state) => googleTemplates(state).toJS();

export const getYoutubeTemplates = (state) => youtubeTemplates(state).toJS();

export const getCustomTemplates = (state) => customTemplates(state).toJS();

export const getScrollerTemplates = (state) => scrollerTemplates(state).toJS();


export const getCalendarAssetTemplates = (state) =>
  calendarAssetTemplates(state).toJS();

export const getDataAssetTemplates = (state) =>
  dataAssetTemplates(state).toJS();

const tags = (state) =>
  state.tags
    .toList()
    .sortBy((tag) => tag.get("count"))
    .reverse();

export const getTags = (state) => tags(state).toJS();

export const getFilterByAspect = (state) => state.filters.assets.aspect;

export const getFilterByType = (state) => state.filters.assets.type;

export const selectedTags = (state) =>
  tags(state)
    .filter((tag) => tag.get("selected"))
    .map((tag) => tag.get("name"));

export const getPreloadState = (state) =>
  state.paginationState.assetPage.nextState;
  
  export const isFetchingAssests = (state) => state.assets.isFetching;



// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./src/selectors/assets.js